'use client';

import React, { useState, useEffect } from 'react';
import type { RateInput, RateBreakdown } from '@/lib/rateCalculator';
import {
  formatCurrency,
  PLATFORM_CONTENT_TYPES,
  calculateRate,
} from '@/lib/rateCalculator';
import AppLogo from '@/components/ui/AppLogo';
import {
  Download, Copy, Share2, Edit3, CheckCircle, Zap, Star, Users,
  Activity, MapPin, Mail, Globe, TrendingUp, Award, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { Instagram, Youtube } from '@/components/ui/SocialIcons';
import { useLanguage } from '@/context/LanguageContext';
import Icon from '@/components/ui/AppIcon';


const DEFAULT_INPUT: RateInput = {
  platform: 'instagram',
  followers: 87400,
  engagementRate: 5.2,
  contentType: 'reel',
  niche: 'fitness',
  usageRights: false,
  exclusivity: false,
};

interface CreatorProfile {
  handle: string;
  displayName: string;
  bio: string;
  location: string;
  email: string;
  website: string;
}

function TikTokSVG({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M16.5 5.5c.4 1.5 1.5 2.5 3 2.8v2.5c-1.2-.1-2.3-.5-3-1.2v5.4c0 2.8-2.2 5-5 5s-5-2.2-5-5 2.2-5 5-5c.3 0 .6 0 .9.1v2.6c-.3-.1-.6-.1-.9-.1-1.4 0-2.5 1.1-2.5 2.5s1.1 2.5 2.5 2.5 2.5-1.1 2.5-2.5V5.5h2.5Z" />
    </svg>
  );
}

const platformIcons: Record<string, React.ReactNode> = {
  instagram: <Instagram size={16} />,
  tiktok: <TikTokSVG size={16} />,
  youtube: <Youtube size={16} />,
};

const platformColors: Record<string, string> = {
  instagram: 'text-pink-400',
  tiktok: 'text-cyan-400',
  youtube: 'text-red-400',
};

export default function RateCardDisplay() {
  const { t } = useLanguage();
  const [input, setInput] = useState<RateInput>(DEFAULT_INPUT);
  const [breakdown, setBreakdown] = useState<RateBreakdown>(() => calculateRate(DEFAULT_INPUT));
  const [isEditing, setIsEditing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [profile, setProfile] = useState<CreatorProfile>({
    handle: '@yourhandle',
    displayName: 'Your Creator Name',
    bio: 'Content creator specializing in authentic brand storytelling.',
    location: 'New York, NY',
    email: 'hello@yourcreator.com',
    website: 'yourcreator.com',
  });
  const [editProfile, setEditProfile] = useState<CreatorProfile>(profile);

  const engagementLabels: Record<string, { label: string; color: string }> = {
    excellent: { label: t.breakdown.excellent, color: 'text-positive' },
    good: { label: t.breakdown.good, color: 'text-accent' },
    average: { label: t.breakdown.average, color: 'text-info' },
    'below-average': { label: t.breakdown.belowAvg, color: 'text-danger' },
  };

  useEffect(() => {
    const savedInput = localStorage.getItem('vv18_rate_input');
    const savedBreakdown = localStorage.getItem('vv18_rate_breakdown');
    if (savedInput && savedBreakdown) {
      try {
        const parsedInput = JSON.parse(savedInput) as RateInput;
        const parsedBreakdown = JSON.parse(savedBreakdown) as RateBreakdown;
        setInput(parsedInput);
        setBreakdown(parsedBreakdown);
      } catch {
        // keep defaults
      }
    }
  }, []);

  const allContentTypes = PLATFORM_CONTENT_TYPES[input.platform];
  const allRates = allContentTypes.map((ct) => {
    const ctBreakdown = calculateRate({ ...input, contentType: ct });
    return {
      contentType: ct,
      label: t.contentTypes[ct],
      rate: ctBreakdown.finalRate,
      low: ctBreakdown.suggestedLow,
      high: ctBreakdown.suggestedHigh,
      isSelected: ct === input.contentType,
    };
  });

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      toast.success(t.rateCard.linkCopied);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleSaveProfile = () => {
    setProfile(editProfile);
    setIsEditing(false);
    toast.success(t.rateCard.profileSaved);
  };

  const followerDisplay =
    input.followers >= 1000000
      ? `${(input.followers / 1000000).toFixed(1)}M`
      : input.followers >= 1000
      ? `${(input.followers / 1000).toFixed(1)}K`
      : String(input.followers);

  const platformName = input.platform.charAt(0).toUpperCase() + input.platform.slice(1);
  const engagementCfg = engagementLabels[breakdown.engagementStatus] ?? engagementLabels['average'];

  const editFields: { key: keyof CreatorProfile; label: string; placeholder: string }[] = [
    { key: 'displayName', label: t.rateCard.displayName, placeholder: 'Your Creator Name' },
    { key: 'handle', label: t.rateCard.handle, placeholder: '@yourhandle' },
    { key: 'bio', label: t.rateCard.bio, placeholder: 'What you create...' },
    { key: 'location', label: t.rateCard.location, placeholder: 'City, Country' },
    { key: 'email', label: t.rateCard.email, placeholder: 'hello@you.com' },
    { key: 'website', label: t.rateCard.website, placeholder: 'yoursite.com' },
  ];

  return (
    <div className="flex flex-col gap-6">
      {/* Action Bar */}
      <div className="no-print flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-foreground">{t.rateCard.yourRateCard}</h2>
          <p className="text-sm text-muted-foreground mt-0.5">{t.rateCard.shareWithBrands}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => { setEditProfile(profile); setIsEditing(true); }}
            className="btn-ghost flex items-center gap-2 px-4 py-2 text-sm"
          >
            <Edit3 size={14} />{t.rateCard.customize}
          </button>
          <button onClick={handleCopyLink} className="btn-ghost flex items-center gap-2 px-4 py-2 text-sm">
            {copied ? <CheckCircle size={14} className="text-positive" /> : <Copy size={14} />}
            {copied ? t.rateCard.copied : t.rateCard.copyLink}
          </button>
          <button onClick={() => window.print()} className="btn-accent flex items-center gap-2 px-4 py-2 text-sm">
            <Download size={14} />{t.rateCard.downloadPDF}
          </button>
        </div>
      </div>

      {/* Edit Modal */}
      {isEditing && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)' }}
          role="dialog"
          aria-modal="true"
          aria-label={t.rateCard.customizeTitle}
        >
          <div className="card-elevated p-6 w-full max-w-md relative" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setIsEditing(false)}
              className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Close modal"
            >
              <X size={18} />
            </button>
            <h3 className="text-lg font-bold text-foreground mb-1">{t.rateCard.customizeTitle}</h3>
            <p className="text-xs text-muted-foreground mb-5">{t.rateCard.customizeSubtitle}</p>
            <div className="flex flex-col gap-4 max-h-[60vh] overflow-y-auto pr-1 scrollbar-hide">
              {editFields.map((field) => (
                <div key={`edit-field-${field.key}`}>
                  <label
                    htmlFor={`edit-${field.key}`}
                    className="block text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wider"
                  >
                    {field.label}
                  </label>
                  <input
                    id={`edit-${field.key}`}
                    type={field.key === 'email' ? 'email' : 'text'}
                    value={editProfile[field.key]}
                    onChange={(e) => setEditProfile((prev) => ({ ...prev, [field.key]: e.target.value }))}
                    placeholder={field.placeholder}
                    className="input-field w-full px-3 py-2.5 text-sm"
                  />
                </div>
              ))}
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setIsEditing(false)} className="btn-ghost flex-1 py-2.5 text-sm">
                {t.rateCard.cancel}
              </button>
              <button onClick={handleSaveProfile} className="btn-primary flex-1 py-2.5 text-sm">
                {t.rateCard.saveChanges}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rate Card */}
      <div
        className="rounded-2xl overflow-hidden shadow-2xl border border-primary/20"
        style={{ background: 'linear-gradient(160deg, #0F0A1E 0%, #1A0A3E 40%, #0F0A1E 100%)' }}
      >
        {/* Header */}
        <div className="relative px-6 sm:px-8 py-8 border-b border-border/50 overflow-hidden">
          <div
            className="absolute top-0 right-0 w-72 h-72 rounded-full pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgba(124,58,237,0.25) 0%, transparent 70%)', transform: 'translate(30%,-30%)' }}
          />
          <div className="relative flex flex-col sm:flex-row items-start sm:items-center gap-6">
            <div className="w-20 h-20 rounded-2xl bg-gradient-primary flex items-center justify-center flex-shrink-0 glow-primary">
              <span className="text-3xl font-extrabold text-white">
                {profile.displayName.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <h2 className="text-2xl font-extrabold text-foreground">{profile.displayName}</h2>
                <span className="niche-badge bg-primary/20 border border-primary/30 text-primary">
                  <Star size={9} />
                  {t.niches[input.niche]}
                </span>
              </div>
              <div className="text-sm text-accent font-semibold mt-0.5">{profile.handle}</div>
              <p className="text-sm text-muted-foreground mt-1.5 max-w-sm leading-relaxed">{profile.bio}</p>
              <div className="flex items-center gap-4 mt-3 flex-wrap">
                <span className="flex items-center gap-1.5 text-xs text-muted-foreground"><MapPin size={11} />{profile.location}</span>
                <span className="flex items-center gap-1.5 text-xs text-muted-foreground"><Mail size={11} />{profile.email}</span>
                <span className="flex items-center gap-1.5 text-xs text-muted-foreground"><Globe size={11} />{profile.website}</span>
              </div>
            </div>
            <div className="flex items-center gap-1.5 opacity-50 flex-shrink-0 no-print">
              <AppLogo size={18} />
              <span className="text-xs font-bold text-muted-foreground">VoltVision18</span>
            </div>
          </div>
        </div>

        {/* Platform Stats */}
        <div className="px-6 sm:px-8 py-6 border-b border-border/50">
          <div className="flex items-center gap-2 mb-4">
            <span className={platformColors[input.platform]}>{platformIcons[input.platform]}</span>
            <h3 className="text-xs font-bold text-foreground uppercase tracking-widest">{platformName} Stats</h3>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { icon: Users, label: t.rateCard.followers, value: followerDisplay, sub: breakdown.audienceTier, color: 'text-primary' },
              { icon: Activity, label: t.rateCard.engagementRate, value: `${input.engagementRate}%`, sub: engagementCfg.label, color: engagementCfg.color },
              {
                icon: TrendingUp,
                label: t.rateCard.avgReach,
                value: breakdown.estimatedReach >= 1000
                  ? `${(breakdown.estimatedReach / 1000).toFixed(1)}K`
                  : String(breakdown.estimatedReach),
                sub: 'per post',
                color: 'text-info',
              },
              { icon: Award, label: t.rateCard.audienceTier, value: `+${Math.round(breakdown.nichePremium * 100)}%`, sub: t.niches[input.niche], color: 'text-accent' },
            ].map((s) => {
              const Icon = s.icon;
              return (
                <div key={`stat-${s.label}`} className="rounded-xl border border-border/60 bg-muted/10 p-4">
                  <div className="flex items-center gap-1.5 mb-2">
                    <Icon size={11} className="text-muted-foreground" />
                    <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">{s.label}</span>
                  </div>
                  <div className={`text-xl font-extrabold font-tabular ${s.color}`}>{s.value}</div>
                  <div className="text-[10px] text-muted-foreground mt-0.5">{s.sub}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Rates Table */}
        <div className="px-6 sm:px-8 py-6 border-b border-border/50">
          <div className="flex items-center gap-2 mb-4">
            <Zap size={13} className="text-accent" />
            <h3 className="text-xs font-bold text-foreground uppercase tracking-widest">{t.rateCard.contentRates}</h3>
            <span className="ml-auto text-[10px] text-muted-foreground">USD · {new Date().getFullYear()}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[360px]">
              <thead>
                <tr className="border-b border-border/50">
                  {[t.rateCard.contentType, t.breakdown.floor, t.breakdown.target, t.breakdown.ceiling].map((h, i) => (
                    <th
                      key={`th-${h}`}
                      className={`pb-3 text-[10px] font-bold text-muted-foreground uppercase tracking-wider ${i === 0 ? 'text-left' : 'text-right'}`}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {allRates.map((row) => (
                  <tr
                    key={`rate-${row.contentType}`}
                    className={`border-b border-border/30 last:border-0 transition-colors ${row.isSelected ? 'bg-primary/5' : 'hover:bg-muted/10'}`}
                  >
                    <td className="py-3.5 pr-4">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-foreground">{row.label}</span>
                        {row.isSelected && (
                          <span className="niche-badge bg-accent/20 border border-accent/30 text-accent" style={{ fontSize: '9px' }}>
                            {t.rateCard.recommended}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-3.5 text-right text-sm text-muted-foreground font-tabular">{formatCurrency(row.low)}</td>
                    <td className="py-3.5 text-right">
                      <span className={`text-sm font-bold font-tabular ${row.isSelected ? 'text-accent' : 'text-foreground'}`}>
                        {formatCurrency(row.rate)}
                      </span>
                    </td>
                    <td className="py-3.5 text-right text-sm text-muted-foreground font-tabular">{formatCurrency(row.high)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {[
              { dot: 'bg-positive', text: `${t.rateCard.usageRights}: +25%` },
              { dot: 'bg-accent', text: `${t.rateCard.exclusivity}: +30%` },
              { dot: 'bg-primary', text: 'Discounts for long-term partnerships' },
            ].map((item) => (
              <div key={`note-${item.text}`} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border/60 bg-muted/10 text-xs text-muted-foreground">
                <span className={`w-2 h-2 rounded-full ${item.dot}`} />
                {item.text}
              </div>
            ))}
          </div>
        </div>

        {/* Card Footer */}
        <div className="px-6 sm:px-8 py-5 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <AppLogo size={18} />
            <span className="text-xs text-muted-foreground">
              {t.rateCard.poweredBy}
            </span>
          </div>
          <p className="text-[10px] text-muted-foreground text-right leading-relaxed">
            {t.rateCard.validFor}
          </p>
        </div>
      </div>

      {/* Save CTA */}
      <div className="no-print card-elevated p-5 flex flex-col sm:flex-row items-center justify-between gap-4">
        <div>
          <h3 className="text-sm font-bold text-foreground">{t.rateCard.yourRateCard}</h3>
          <p className="text-xs text-muted-foreground mt-0.5">{t.rateCard.shareWithBrands}</p>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <a href="/sign-up-login" className="btn-primary flex items-center gap-2 px-5 py-2.5 text-sm">
            <Zap size={14} />{t.auth.createAccount}
          </a>
          <button onClick={handleCopyLink} className="btn-ghost flex items-center gap-2 px-4 py-2.5 text-sm">
            <Share2 size={14} />{t.rateCard.copyLink}
          </button>
        </div>
      </div>
    </div>
  );
}